import React from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { ErrorMessage, Field, FormFooter } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { type ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { isMaxCharacterCount, isRequired, useValidators } from '@atlassian/eoc-common';
import {
	EXTRA_SELECT_PROPS,
	type TimezoneOption,
	TimezoneSelect,
} from '@atlassian/eoc-schedule-common';

import { SCHEDULE_NAME_MAX_LENGTH } from '../../../common/constants';
import { messages as commonMessages } from '../../../common/messages';
import { PageModalType } from '../../../common/types';
import { useActions as usePageActions } from '../../../controllers/page-store';
import { CreateVariant } from '../../../controllers/page-store/state/types';
import { useSectionUiPreferences } from '../../../controllers/preferences';
import { useCloneScheduleService, useCreateScheduleService } from '../../../controllers/section';

import { messages } from './messages';
import { FlexWrapper, StyledForm } from './styled';
import type { CreateScheduleProps, FormFields } from './types';
import { useDefaultValues } from './utils';

const CreateSchedule: React.FC<CreateScheduleProps> = ({ baseSchedule, variant }) => {
	const intlApi = useIntl();
	const { closePageModal } = usePageActions();
	const { createSchedule, isLoading: createScheduleLoading } = useCreateScheduleService();
	const { cloneSchedule, isLoading: cloneScheduleLoading } = useCloneScheduleService();
	const nameValidator = useValidators([
		isRequired(),
		isMaxCharacterCount(SCHEDULE_NAME_MAX_LENGTH),
	]);
	const { schedulesCollapsed, partialUpdate: updateSectionPreferences } = useSectionUiPreferences();

	const defaultValues = useDefaultValues({
		base: baseSchedule,
		variant,
	});

	return (
		<FlexWrapper alignItems="center" flex="1 1 auto" flexDirection="column">
			<Heading as="h2" size="large">
				{intlApi.formatMessage(messages.createSchedule)}
			</Heading>
			<Form<FormFields>
				onSubmit={(values) => {
					if (variant === CreateVariant.Create) {
						if (schedulesCollapsed) {
							updateSectionPreferences({
								key: 'schedulesCollapsed',
								value: false,
							});
						}

						createSchedule({
							description: values.description,
							name: values.name,
							timezone: values.timezone.value,
						});
					} else if (variant === CreateVariant.Clone) {
						cloneSchedule({
							description: values.description ?? '',
							name: values.name,
							timezone: values.timezone.value,
							enabled: baseSchedule!.enabled,
							id: baseSchedule!.id,
						});
					}
				}}
			>
				{({ formProps }) => (
					<StyledForm {...formProps}>
						<Field
							label={intlApi.formatMessage(messages.scheduleName)}
							name="name"
							isRequired
							validate={nameValidator}
							defaultValue={defaultValues.name}
						>
							{({ fieldProps, error }) => (
								<>
									<Textfield {...fieldProps} />
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>
						<Field
							label={intlApi.formatMessage(messages.description)}
							name="description"
							defaultValue={defaultValues.description}
							// TODO: add length validation (description)
						>
							{({ fieldProps }) => <Textfield {...fieldProps} />}
						</Field>
						<Field<ValueType<TimezoneOption>>
							defaultValue={defaultValues.timezone}
							label={intlApi.formatMessage(messages.timezone)}
							name="timezone"
						>
							{({ fieldProps }) => <TimezoneSelect {...EXTRA_SELECT_PROPS} {...fieldProps} />}
						</Field>
						<FormFooter>
							<ButtonGroup>
								<Button
									appearance="subtle"
									onClick={() => closePageModal(PageModalType.CreateSchedule)}
								>
									{intlApi.formatMessage(commonMessages.cancel)}
								</Button>
								<Button
									appearance="primary"
									isLoading={createScheduleLoading || cloneScheduleLoading}
									type="submit"
									onClick={(_, analyticsEvent) => {
										fireUIAnalytics(analyticsEvent, 'addScheduleInModal');
									}}
								>
									{intlApi.formatMessage(messages.create)}
								</Button>
							</ButtonGroup>
						</FormFooter>
					</StyledForm>
				)}
			</Form>
		</FlexWrapper>
	);
};

export { CreateSchedule };
